import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static values = {
    buttonCount: Number,
    buttonSpacing: Number,
    xAmplitude: Number,
    buttonsPerWave: Number,
    centerX: Number,
  };

  connect() {
    this.isLoading = false;
    this.currentButtonCount = this.buttonCountValue;
    this.setupInfiniteScroll();
    this.setupSmoothScrolling();
  }

  setupInfiniteScroll() {
    this.element.addEventListener('scroll', this.handleScroll.bind(this));
  }

  setupSmoothScrolling() {
    this.element.style.scrollBehavior = 'smooth';
  }

  handleScroll() {
    if (this.isLoading) return;

    const scrollTop = this.element.scrollTop;
    const scrollHeight = this.element.scrollHeight;
    const clientHeight = this.element.clientHeight;

    // Load more content when user scrolls to 80% of the content
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      this.loadMoreTrailButtons();
    }
  }

  loadMoreTrailButtons() {
    this.isLoading = true;

    // Simulate loading more trail buttons (in real app, this would be an AJAX call)
    setTimeout(() => {
      const buttonsToAdd = 10;
      const trailContent = this.element.querySelector('#trail-content');

      for (let i = 0; i < buttonsToAdd; i++) {
        const buttonIndex = this.currentButtonCount + i;
        const yPosition = buttonIndex * this.buttonSpacingValue + 60;
        const wavePhase = (buttonIndex / this.buttonsPerWaveValue) * 2 * Math.PI;
        const xPosition = this.centerXValue + Math.sin(wavePhase) * this.xAmplitudeValue;
        const finalY = yPosition;

        // Create new button element
        const buttonDiv = this.createButtonElement(buttonIndex, xPosition, finalY);

        // Add connection line to previous button if exists
        if (buttonIndex > 0) {
          const lineDiv = this.createConnectionLine(buttonIndex, xPosition, finalY);
          buttonDiv.appendChild(lineDiv);
        }

        trailContent.appendChild(buttonDiv);
      }

      this.currentButtonCount += buttonsToAdd;

      // Increase container height to accommodate new content
      const newHeight = this.currentButtonCount * this.buttonSpacingValue + 500;
      trailContent.style.minHeight = `${newHeight}px`;

      this.isLoading = false;
    }, 500);
  }

  createButtonElement(buttonIndex, xPosition, finalY) {
    const buttonDiv = document.createElement('div');
    buttonDiv.className = 'tw-absolute tw-z-30 tw-transition-all tw-duration-300';
    buttonDiv.style.left = `${xPosition - 25}px`;
    buttonDiv.style.top = `${finalY}px`;
    buttonDiv.setAttribute('data-trail-button', buttonIndex);

    // Add button content (simplified - in real app would render component)
    const buttonTypes = ['done', 'expired', 'end_soon', 'open'];
    const randomType = buttonTypes[Math.floor(Math.random() * buttonTypes.length)];

    buttonDiv.innerHTML = `
      <div class="tw-w-12 tw-h-12 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-white tw-cursor-pointer tw-transition-transform hover:tw-scale-110 ${this.getButtonColorClass(
        randomType,
      )}">
        ${buttonIndex + 1}
      </div>
    `;

    return buttonDiv;
  }

  generateWavePath(height) {
    let path = 'M400,0';
    const segments = Math.ceil(height / 400);

    for (let i = 0; i < segments; i++) {
      const y1 = i * 400 + 200;
      const y2 = i * 400 + 400;
      const isEven = i % 2 === 0;

      // Gentler curves with reduced control point distance
      if (isEven) {
        path += ` Q330,${y1} 400,${y2}`;
      } else {
        path += ` Q470,${y1} 400,${y2}`;
      }
    }

    return path;
  }

  getButtonColorClass(type) {
    switch (type) {
      case 'done':
        return 'tw-bg-green-500';
      case 'expired':
        return 'tw-bg-red-500';
      case 'end_soon':
        return 'tw-bg-yellow-500';
      case 'open':
        return 'tw-bg-blue-500';
      default:
        return 'tw-bg-gray-500';
    }
  }
}
